/**
 * 업무 프로젝트(PMS) J3c2a — 업무 상세 서랍의 «댓글» 탭.
 *
 * 목록(오래된 순)·작성(@멘션)·내 댓글 수정·삭제(soft)를 맡는다. 고른 탭일 때만 마운트되므로
 * 서랍을 열었다고 해서 댓글을 읽지는 않는다.
 * - 🔴 현재 사용자(me)는 모듈 최상위가 아니라 컴포넌트 안에서 읽는다 — 최상위에서 읽으면 import
 *   시점(로그인 전)의 빈 값으로 굳어 '내 댓글' 판정이 영영 거짓이 된다.
 * - 🔴 htmlToText·textToHtml 은 업무 상세에서 가져다 쓴다(같은 규칙). 그쪽이 이 파일을 다시
 *   import 하므로 순환이다 → 모듈 최상위에서 부르지 않는다(핸들러·렌더 안에서만 부른다).
 * - 삭제된 댓글도 서버가 자리를 남긴다(body_html='' · mentions=[] · deleted=true) → 한 줄 안내만.
 * - 수정 화면에서 멘션은 바꾸지 않는다 — PATCH 본문에 mention_user_ids 키를 아예 넣지 않으면
 *   서버가 기존 멘션을 그대로 둔다(빈 배열을 보내면 전부 해제된다).
 * - 실패 문구는 지어내지 않고 서버 detail 을 그대로 보인다(errorText 로 문자열화).
 */
import { useEffect, useState } from 'react';
import { taskPmAPI } from '../../api/client';
import { alertDialog, confirmDialog, toast } from '../../components/AppDialog';
import { errorText } from './errorText';
import { htmlToText, textToHtml } from './TaskPmTaskDetail';
import { fmtMsgTime } from '../../utils/msgDate';
import { COLORS, badge, btn, input } from '../../utils/uiStyles';

const LOAD_FAIL = '댓글을 불러오지 못했습니다.';
const SAVE_FAIL = '댓글을 저장하지 못했습니다.';
const DELETE_FAIL = '댓글을 지우지 못했습니다.';
const DELETE_CONFIRM = '이 댓글을 지울까요?';

const personName = (userId, name) => name || `사용자 #${userId}`;
const memberName = (m) => m.name || m.email || `사용자 #${m.user_id}`;

const subText = { fontSize: '11px', color: COLORS.textSub };
const mutedLine = { margin: 0, fontSize: '12px', color: COLORS.textSub };
const commentRow = { padding: '8px 0', borderBottom: `1px solid ${COLORS.borderSoft}` };

export default function TaskPmComments({ taskId, project, canWrite }) {
  // 🔴 렌더마다 읽는다(모듈 최상위 상수 금지 — 위 주석 참조).
  const me = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const myId = me?.id;

  const [items, setItems] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [draft, setDraft] = useState('');
  const [mentionIds, setMentionIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState({ id: null, value: '' });   // id=null = 편집 중 아님

  useEffect(() => {
    let alive = true;
    taskPmAPI.listComments(taskId)
      .then((res) => {
        if (!alive) return;
        setItems(res?.data?.items || []);
        setLoadError('');
      })
      .catch((e) => {
        if (!alive) return;
        setItems([]);
        setLoadError(errorText(e, LOAD_FAIL));
      });
    return () => { alive = false; };
  }, [taskId, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const reportError = (e, fallback) => {
    const message = errorText(e, fallback);
    if (e?.response?.status) alertDialog(message); else toast(message, 'error');
  };

  const submit = async () => {
    if (busy || !draft.trim()) return;
    setBusy(true);
    try {
      await taskPmAPI.addComment(taskId, {
        body_html: textToHtml(draft),
        mention_user_ids: mentionIds,
      });
      setDraft('');
      setMentionIds([]);
      reload();
    } catch (e) {
      reportError(e, SAVE_FAIL);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (busy || edit.id === null || !edit.value.trim()) return;
    const sentId = edit.id;
    setBusy(true);
    try {
      // 🔴 mention_user_ids 는 보내지 않는다 = 기존 멘션 유지.
      await taskPmAPI.updateComment(sentId, { body_html: textToHtml(edit.value) });
      // 보낸 댓글의 편집일 때만 닫는다 — 그사이 연 다른 댓글의 편집을 닫지 않는다.
      setEdit((prev) => (prev.id === sentId ? { id: null, value: '' } : prev));
      reload();
    } catch (e) {
      reportError(e, SAVE_FAIL);
    } finally {
      setBusy(false);
    }
  };

  const removeComment = async (commentId) => {
    if (!(await confirmDialog(DELETE_CONFIRM))) return;
    try {
      await taskPmAPI.deleteComment(commentId);     // 204 — 목록을 다시 읽는다
      if (edit.id === commentId) setEdit({ id: null, value: '' });
      reload();
    } catch (e) {
      reportError(e, DELETE_FAIL);
    }
  };

  const canEdit = (c) => Boolean(canWrite) && c.author_id === myId;
  const canDelete = (c) => Boolean(canWrite) && (c.author_id === myId || project?.my_role === 'manager');

  // 자기 자신은 언급 후보가 아니다(서버도 자기 자신은 조용히 뺀다).
  const candidates = (project?.members || [])
    .filter((m) => m.user_id !== myId)
    .map((m) => ({ id: m.user_id, name: memberName(m) }));

  return (
    <div>
      {loadError && (
        <p role="alert" style={{ margin: '0 0 8px', fontSize: '12px', color: COLORS.dangerText }}>{loadError}</p>
      )}

      <ul aria-label="댓글 목록" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {items.map((c) => (
          <li key={c.id} style={commentRow}>
            {c.deleted
              ? <p style={mutedLine}>삭제된 댓글입니다.</p>
              : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <strong style={{ fontSize: '13px', color: COLORS.text }}>{personName(c.author_id, c.author_name)}</strong>
                    <span style={subText}>{fmtMsgTime(c.created_at)}</span>
                    {c.updated_at && <span style={subText}>(수정됨)</span>}
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                      {/* 🔴 행마다 되풀이되는 버튼이라 이름은 aria-label 로 구별한다. */}
                      {canEdit(c) && edit.id !== c.id && (
                        <button type="button" aria-label={`댓글 ${c.id} 수정`} style={btn('secondary', 'sm')}
                          onClick={() => setEdit({ id: c.id, value: htmlToText(c.body_html) })}>수정</button>
                      )}
                      {canDelete(c) && (
                        <button type="button" aria-label={`댓글 ${c.id} 삭제`} style={btn('danger', 'sm')}
                          onClick={() => removeComment(c.id)}>삭제</button>
                      )}
                    </span>
                  </div>

                  {edit.id === c.id
                    ? (
                      <>
                        {/* 저장 중에는 읽기 전용 — 그사이 친 글자가 저장 응답으로 닫히며 사라지지 않게. */}
                        <textarea aria-label={`댓글 ${c.id} 내용`} rows={3} value={edit.value}
                          readOnly={busy} aria-busy={busy}
                          onChange={(e) => setEdit({ id: c.id, value: e.target.value })}
                          style={{ ...input, resize: 'vertical' }} />
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                          <button type="button" aria-label={`댓글 ${c.id} 취소`} style={btn('secondary', 'sm')}
                            disabled={busy}
                            onClick={() => setEdit({ id: null, value: '' })}>취소</button>
                          <button type="button" aria-label={`댓글 ${c.id} 저장`} onClick={saveEdit}
                            disabled={busy || !edit.value.trim()}
                            style={{ ...btn('primary', 'sm'), opacity: !busy && edit.value.trim() ? 1 : 0.5 }}>저장</button>
                        </div>
                      </>
                    )
                    : (
                      // 서버가 정화한 HTML 을 그대로 그린다(업무 설명과 같은 허용 목록).
                      <div style={{ fontSize: '13px', color: COLORS.text, wordBreak: 'break-word' }}
                        dangerouslySetInnerHTML={{ __html: c.body_html || '' }} />
                    )}

                  {(c.mentions || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                      <span style={subText}>언급</span>
                      {c.mentions.map((m) => (
                        <span key={m.user_id} style={badge('default')}>{personName(m.user_id, m.name)}</span>
                      ))}
                    </div>
                  )}
                </>
              )}
          </li>
        ))}
        {items.length === 0 && !loadError && <li style={mutedLine}>댓글이 없습니다.</li>}
      </ul>

      {canWrite && (
        <div style={{ marginTop: '10px' }}>
          {/* Enter 는 줄바꿈이다(등록은 버튼으로만). 이름은 상세의 다른 입력칸과 겹치지 않게 «새 댓글». */}
          {/* 저장 중에는 입력·멘션을 잠근다 — 성공하면 비우므로 그사이 친 글자·고른 멘션이 사라진다. */}
          <textarea aria-label="새 댓글" rows={3} placeholder="댓글을 입력하세요" value={draft}
            readOnly={busy} aria-busy={busy}
            onChange={(e) => setDraft(e.target.value)}
            style={{ ...input, resize: 'vertical' }} />
          {candidates.length > 0 && (
            <ul aria-label="언급할 사람" style={{ listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '6px 0 0', padding: 0 }}>
              {candidates.map((c) => (
                <li key={c.id} style={{ fontSize: '12px' }}>
                  <label>
                    <input type="checkbox" aria-label={`${c.name} 언급`} checked={mentionIds.includes(c.id)}
                      disabled={busy}
                      onChange={(e) => setMentionIds(e.target.checked
                        ? [...mentionIds, c.id]
                        : mentionIds.filter((id) => id !== c.id))} />
                    {c.name}
                  </label>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: 'flex', marginTop: '6px' }}>
            <button type="button" onClick={submit} disabled={busy || !draft.trim()}
              style={{ ...btn('primary', 'sm'), marginLeft: 'auto', opacity: !busy && draft.trim() ? 1 : 0.5 }}>댓글 등록</button>
          </div>
        </div>
      )}
    </div>
  );
}
